import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  TrendingUp, TrendingDown, DollarSign, Receipt, 
  Calendar, AlertTriangle, ArrowUpRight, ArrowDownRight,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "../services/authService";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, ComposedChart
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FiscalDashboard() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState(null);
  const [fatturato, setFatturato] = useState([]);
  const [ivaTrimestrale, setIvaTrimestrale] = useState([]);
  const [cashFlow, setCashFlow] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [kpiRes, fatturatoRes, ivaRes, cashFlowRes] = await Promise.all([
        authFetch(`${API}/fiscal/dashboard/kpi?year=${selectedYear}`, { }),
        authFetch(`${API}/fiscal/dashboard/fatturato-mensile?year=${selectedYear}`, { }),
        authFetch(`${API}/fiscal/dashboard/iva-trimestrale?year=${selectedYear}`, { }),
        authFetch(`${API}/fiscal/dashboard/cash-flow?year=${selectedYear}`, { })
      ]);

      if (kpiRes.ok) setKpi(await kpiRes.json());
      if (fatturatoRes.ok) setFatturato(await fatturatoRes.json());
      if (ivaRes.ok) setIvaTrimestrale(await ivaRes.json());
      if (cashFlowRes.ok) setCashFlow(await cashFlowRes.json());
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Errore nel caricamento dashboard");
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "blue" }) => (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-zinc-400">{title}</p>
            <p className={`text-2xl font-bold text-${color}-400 mt-1`}>
              {typeof value === 'number' ? formatCurrency(value) : value}
            </p>
            {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-500/20`}>
            <Icon className={`w-6 h-6 text-${color}-400`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center mt-3 text-sm ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-4 h-4 mr-1" /> : <ArrowDownRight className="w-4 h-4 mr-1" />}
            <span>{Math.abs(trendValue || trend)}%</span>
            <span className="text-zinc-500 ml-2">vs anno prec.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="fiscal-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard Fiscale</h1>
          <p className="text-zinc-400 mt-1">Panoramica finanziaria e previsioni</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700" data-testid="year-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchDashboardData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {kpi && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Fatturato Totale"
            value={kpi.fatturato_totale}
            subtitle={`${kpi.num_fatture} fatture emesse`}
            icon={DollarSign}
            color="green"
          />
          <KPICard
            title="Utile Lordo"
            value={kpi.utile_lordo}
            subtitle={`Margine ${kpi.margine_percentuale}%`}
            icon={kpi.utile_lordo >= 0 ? TrendingUp : TrendingDown}
            color={kpi.utile_lordo >= 0 ? "green" : "red"}
          />
          <KPICard
            title="Saldo IVA"
            value={kpi.saldo_iva}
            subtitle={kpi.saldo_iva >= 0 ? "Da versare" : "A credito"}
            icon={Receipt}
            color={kpi.saldo_iva >= 0 ? "orange" : "blue"}
          />
          <KPICard
            title="Cash Flow Previsto"
            value={kpi.cash_flow_previsto}
            subtitle={`${kpi.scadenze_aperte} scadenze aperte`}
            icon={kpi.scadenze_scadute > 0 ? AlertTriangle : Calendar}
            color={kpi.scadenze_scadute > 0 ? "red" : "blue"}
          />
        </div>
      )}

      {/* Alert scadenze */}
      {kpi && kpi.scadenze_scadute > 0 && (
        <Card className="bg-red-950/30 border-red-800">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">
              <strong>{kpi.scadenze_scadute}</strong> scadenze oltre la data di pagamento! 
              Incassi attesi: {formatCurrency(kpi.incassi_attesi)}, 
              Pagamenti dovuti: {formatCurrency(kpi.pagamenti_dovuti)}
            </span>
          </CardContent>
        </Card>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fatturato Mensile */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              Andamento Fatturato Mensile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={fatturato}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="mese" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => `€${v/1000}k`} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  formatter={(value) => [formatCurrency(value), '']}
                  labelStyle={{ color: '#F9FAFB' }}
                />
                <Legend />
                <Bar dataKey="fatturato" name="Fatturato" fill="#22C55E" radius={[4, 4, 0, 0]} />
                <Bar dataKey="costi" name="Costi" fill="#EF4444" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="utile" name="Utile" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* IVA Trimestrale */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Receipt className="w-5 h-5 text-orange-400" />
              Liquidazione IVA Trimestrale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ivaTrimestrale}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="trimestre" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => `€${v}`} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  formatter={(value) => [formatCurrency(value), '']}
                  labelStyle={{ color: '#F9FAFB' }}
                />
                <Legend />
                <Bar dataKey="iva_debito" name="IVA Debito" fill="#F97316" radius={[4, 4, 0, 0]} />
                <Bar dataKey="iva_credito" name="IVA Credito" fill="#06B6D4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
              {ivaTrimestrale.map((t, i) => (
                <div key={i} className={`p-2 rounded ${t.saldo >= 0 ? 'bg-orange-950/30' : 'bg-cyan-950/30'}`}>
                  <p className="text-xs text-zinc-400">{t.trimestre}</p>
                  <p className={`text-sm font-semibold ${t.saldo >= 0 ? 'text-orange-400' : 'text-cyan-400'}`}>
                    {formatCurrency(t.saldo)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Cash Flow Previsionale */}
        <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-400" />
              Cash Flow Previsionale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={cashFlow}>
                <defs>
                  <linearGradient id="colorEntrate" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorUscite" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="mese" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => `€${v/1000}k`} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  formatter={(value) => [formatCurrency(value), '']}
                  labelStyle={{ color: '#F9FAFB' }}
                />
                <Legend />
                <Area type="monotone" dataKey="entrate" name="Entrate Previste" stroke="#22C55E" fillOpacity={1} fill="url(#colorEntrate)" />
                <Area type="monotone" dataKey="uscite" name="Uscite Previste" stroke="#EF4444" fillOpacity={1} fill="url(#colorUscite)" />
                <Line type="monotone" dataKey="saldo_cumulativo" name="Saldo Cumulativo" stroke="#8B5CF6" strokeWidth={3} dot={{ fill: '#8B5CF6', r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Summary Stats */}
      {kpi && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 text-center">
              <p className="text-zinc-400 text-sm">Fatture Emesse</p>
              <p className="text-2xl font-bold text-white">{kpi.num_fatture}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 text-center">
              <p className="text-zinc-400 text-sm">Documenti Passivi</p>
              <p className="text-2xl font-bold text-white">{kpi.num_documenti_passivi}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 text-center">
              <p className="text-zinc-400 text-sm">Incassi Attesi</p>
              <p className="text-2xl font-bold text-green-400">{formatCurrency(kpi.incassi_attesi)}</p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 text-center">
              <p className="text-zinc-400 text-sm">Pagamenti Dovuti</p>
              <p className="text-2xl font-bold text-red-400">{formatCurrency(kpi.pagamenti_dovuti)}</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
